import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import BillingRateTable from '../components/BillingRateTable';
import './AdminUsers.css';

export default function AdminUsers() {
  const { user: currentUser, impersonate: startImpersonation } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', plan: 'basic', allowed_pages: [] });
  const [accessConfig, setAccessConfig] = useState({ pages: [], plans: [], presets: {} });
  const [viewingUser, setViewingUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [resetCode, setResetCode] = useState(null); // { code, expires_at, user }
  const [smtp, setSmtp] = useState({ host: 'smtp.gmail.com', port: 587, user: '', pass: '', from_name: 'TuWay Africa', from_address: '', has_pass: false });
  const [smtpBusy, setSmtpBusy] = useState(false);
  const [authSettings, setAuthSettings] = useState({ google_enabled: true });
  const [authBusy, setAuthBusy] = useState(false);
  const [featureSettings, setFeatureSettings] = useState({ quick_connect_enabled: true });
  const [featuresBusy, setFeaturesBusy] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await api.get('/admin/users');
      setUsers(r.data.users);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/admin/stats');
      setStats(r.data);
    } catch {}
  }, []);

  const fetchAccessConfig = useCallback(async () => {
    try {
      const r = await api.get('/admin/access-config');
      setAccessConfig(r.data);
    } catch {}
  }, []);

  // System SMTP is super-admin only (server enforces it too)
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const fetchSmtp = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const r = await api.get('/admin/settings/smtp');
      setSmtp(s => ({ ...s, ...r.data.smtp, pass: '' }));
    } catch {}
  }, [isSuperAdmin]);

  const fetchAuthSettings = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const r = await api.get('/admin/settings/auth');
      setAuthSettings(r.data.auth);
    } catch {}
  }, [isSuperAdmin]);

  const fetchFeatureSettings = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const r = await api.get('/admin/settings/features');
      setFeatureSettings(r.data.features);
    } catch {}
  }, [isSuperAdmin]);

  useEffect(() => { fetchUsers(); fetchStats(); fetchAccessConfig(); fetchSmtp(); fetchAuthSettings(); fetchFeatureSettings(); }, [fetchUsers, fetchStats, fetchAccessConfig, fetchSmtp, fetchAuthSettings, fetchFeatureSettings]);

  const toggleGoogleSignIn = async () => {
    const next = !authSettings.google_enabled;
    setAuthBusy(true);
    try {
      const r = await api.put('/admin/settings/auth', { google_enabled: next });
      setAuthSettings(r.data.auth);
      toast.success(`Google sign-in ${next ? 'shown' : 'hidden'}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update sign-in settings'); }
    setAuthBusy(false);
  };

  const toggleQuickConnect = async () => {
    const next = !featureSettings.quick_connect_enabled;
    setFeaturesBusy(true);
    try {
      const r = await api.put('/admin/settings/features', { quick_connect_enabled: next });
      setFeatureSettings(r.data.features);
      toast.success(`Quick Connect ${next ? 'shown' : 'hidden'}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update feature settings'); }
    setFeaturesBusy(false);
  };

  const saveSmtp = async () => {
    setSmtpBusy(true);
    try {
      await api.put('/admin/settings/smtp', smtp);
      toast.success('SMTP settings saved');
      fetchSmtp();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    setSmtpBusy(false);
  };

  const testSmtp = async () => {
    setSmtpBusy(true);
    try {
      const r = await api.post('/admin/settings/smtp/test');
      toast.success(r.data.message || 'Test email sent');
    } catch (err) { toast.error(err.response?.data?.error || 'Test failed', { duration: 8000 }); }
    setSmtpBusy(false);
  };

  const generateResetCode = async (u) => {
    if (!window.confirm(`Generate a password reset code for "${u.name}"? Any previous code stops working.`)) return;
    try {
      const r = await api.post(`/admin/users/${u._id}/reset-code`);
      setResetCode(r.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const toggleActive = async (id) => {
    try {
      await api.patch(`/admin/users/${id}/toggle-active`);
      toast.success('Account status updated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete user "${name}" and ALL their data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User deleted');
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const startEdit = (u) => {
    setEditingUser(u._id);
    const plan = u.plan || 'basic';
    const allowed = Array.isArray(u.allowed_pages) && u.allowed_pages.length
      ? u.allowed_pages
      : (accessConfig.presets[plan] || []);
    setEditForm({ name: u.name, email: u.email, role: u.role, plan, allowed_pages: allowed });
  };

  // Switching plan pre-fills the page checkboxes with that plan's preset.
  const changePlan = (plan) => {
    setEditForm(f => ({ ...f, plan, allowed_pages: accessConfig.presets[plan] || f.allowed_pages }));
  };

  const togglePage = (key) => {
    setEditForm(f => ({
      ...f,
      allowed_pages: f.allowed_pages.includes(key)
        ? f.allowed_pages.filter(p => p !== key)
        : [...f.allowed_pages, key],
    }));
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${editingUser}`, editForm);
      toast.success('User updated');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const viewUser = async (id) => {
    try {
      const r = await api.get(`/admin/users/${id}`);
      setUserDetail(r.data);
      setViewingUser(id);
    } catch { toast.error('Failed to load user details'); }
  };

  const impersonate = async (id) => {
    if (!window.confirm('You will be logged in as this user. Use "Return to my admin account" in the top banner to come back. Continue?')) return;
    try {
      const r = await api.post(`/admin/users/${id}/impersonate`);
      startImpersonation(r.data.token, r.data.user);
      toast.success(`Now logged in as ${r.data.user.name}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  // "5m ago" / "3h ago" / "2d ago" — falls back to the date for older activity
  const relativeTime = (d) => {
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
    if (mins < 7 * 24 * 60) return `${Math.floor(mins / (24 * 60))}d ago`;
    return new Date(d).toLocaleDateString();
  };

  const fmtDateTime = (d) =>
    `${new Date(d).toLocaleDateString()} ${new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="admin-loading">Loading users…</div>;

  return (
    <div className="admin-page">
      <h1>Admin Dashboard</h1>

      {stats && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="stat-number">{stats.totalUsers}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="stat-number">{stats.activeUsers}</div>
            <div className="stat-label">Active Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="stat-number">{stats.totalContacts}</div>
            <div className="stat-label">Total Contacts</div>
          </div>
          <div className="admin-stat-card">
            <div className="stat-number">{stats.totalBroadcasts}</div>
            <div className="stat-label">Total Broadcasts</div>
          </div>
        </div>
      )}

      <div className="admin-toolbar">
        <input
          type="text"
          placeholder="Search users by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="admin-search"
        />
        <span className="admin-count">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Plan</th>
              <th>Status</th>
              <th>WA Connected</th>
              <th>Contacts</th>
              <th>Broadcasts</th>
              {isSuperAdmin && <th>Tokens</th>}
              <th>Joined</th>
              <th>Last Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u._id} className={!u.isActive ? 'row-disabled' : ''}>
                <td>
                  <div className="user-cell">
                    <div className="user-cell-avatar">{u.name?.[0]?.toUpperCase()}</div>
                    <div>
                      <div className="user-cell-name">{u.name}</div>
                      <div className="user-cell-email">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
                <td><span className={`plan-badge plan-${u.plan || 'basic'}`}>{u.plan || 'basic'}</span></td>
                <td>
                  <span className={`status-badge ${u.isActive ? 'active' : 'disabled'}`}>
                    {u.isActive ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td>{u.wa_config?.connected ? '✓' : '—'}</td>
                <td>{u.contactCount}</td>
                <td>{u.broadcastCount}</td>
                {isSuperAdmin && <td><strong>{new Intl.NumberFormat().format(u.token_balance || 0)}</strong></td>}
                <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td>
                  {u.last_active_at ? (
                    <div className="last-active-cell" title={fmtDateTime(u.last_active_at)}>
                      <div>{relativeTime(u.last_active_at)}</div>
                      <div className="user-cell-email">{fmtDateTime(u.last_active_at)}</div>
                    </div>
                  ) : '—'}
                </td>
                <td>
                  <div className="action-btns">
                    <button className="btn-sm btn-view" onClick={() => viewUser(u._id)} title="View details">👁</button>
                    <button className="btn-sm btn-edit" onClick={() => startEdit(u)} title="Edit">✎</button>
                    <button className="btn-sm btn-impersonate" onClick={() => impersonate(u._id)} title="Login as user">🔑</button>
                    <button className="btn-sm btn-view" onClick={() => generateResetCode(u)} title="Generate password reset code">🎫</button>
                    <button className="btn-sm btn-toggle" onClick={() => toggleActive(u._id)} title={u.isActive ? 'Disable' : 'Enable'}>
                      {u.isActive ? '🚫' : '✅'}
                    </button>
                    <button className="btn-sm btn-delete" onClick={() => deleteUser(u._id, u.name)} title="Delete">🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Token billing — super admin only */}
      {isSuperAdmin && <TokenBillingCard users={users} onChanged={fetchUsers} />}

      {isSuperAdmin && <WabaSharesCard users={users} pages={accessConfig.pages} />}

      {/* Public API limits — super admin only */}
      {isSuperAdmin && <ApiLimitsCard users={users} onChanged={fetchUsers} />}

      {/* Authentication options — super admin only */}
      {isSuperAdmin && (
        <div className="card" style={{ marginTop: 24, maxWidth: 640 }}>
          <div className="card-title">Authentication</div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Control which sign-in methods are available on the public sign-in and registration pages.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, cursor: authBusy ? 'wait' : 'pointer' }}>
            <span>
              <strong>Sign in with Google</strong>
              <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                When hidden, Google sign-in and sign-up are also disabled on the server.
              </span>
            </span>
            <input
              type="checkbox"
              checked={authSettings.google_enabled}
              disabled={authBusy}
              onChange={toggleGoogleSignIn}
              aria-label="Show Sign in with Google"
            />
          </label>
        </div>
      )}

      {/* Platform feature visibility — super admin only */}
      {isSuperAdmin && (
        <div className="card" style={{ marginTop: 24, maxWidth: 640 }}>
          <div className="card-title">Platform Features</div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Control optional features available across all workspaces.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, cursor: featuresBusy ? 'wait' : 'pointer' }}>
            <span>
              <strong>WhatsApp Quick Connect</strong>
              <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                Show Meta Embedded Signup in Settings. Manual Setup remains available when hidden.
              </span>
            </span>
            <input
              type="checkbox"
              checked={featureSettings.quick_connect_enabled}
              disabled={featuresBusy}
              onChange={toggleQuickConnect}
              aria-label="Show WhatsApp Quick Connect"
            />
          </label>
        </div>
      )}

      {/* System SMTP for password reset emails — super admin only */}
      {isSuperAdmin && (
      <div className="card" style={{ marginTop: 24, maxWidth: 640 }}>
        <div className="card-title">📧 System Email (SMTP) — password reset emails</div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0, lineHeight: 1.7 }}>
          Used for "forgot password" emails sent to your users. For <strong>Gmail</strong>: keep host <code>smtp.gmail.com</code>,
          port <code>587</code>, your Gmail address as user, and an <strong>App Password</strong> as the password
          (Google Account → Security → 2-Step Verification → App passwords).
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">SMTP host</label>
            <input className="input" value={smtp.host} onChange={e => setSmtp(s => ({ ...s, host: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="label">Port</label>
            <input className="input" type="number" value={smtp.port} onChange={e => setSmtp(s => ({ ...s, port: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">User (Gmail address)</label>
            <input className="input" value={smtp.user} placeholder="you@gmail.com"
              onChange={e => setSmtp(s => ({ ...s, user: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="label">App password {smtp.has_pass && <span style={{ color: 'var(--success)' }}>(saved ✓)</span>}</label>
            <input className="input" type="password" value={smtp.pass}
              placeholder={smtp.has_pass ? 'Leave blank to keep current' : 'xxxx xxxx xxxx xxxx'}
              onChange={e => setSmtp(s => ({ ...s, pass: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">From name</label>
            <input className="input" value={smtp.from_name} onChange={e => setSmtp(s => ({ ...s, from_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="label">From address (optional)</label>
            <input className="input" value={smtp.from_address} placeholder="defaults to the user above"
              onChange={e => setSmtp(s => ({ ...s, from_address: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={saveSmtp} disabled={smtpBusy}>💾 Save settings</button>
          <button className="btn btn-ghost" onClick={testSmtp} disabled={smtpBusy}>✉️ Send test email to me</button>
        </div>
      </div>
      )}

      {/* Reset code modal */}
      {resetCode && (
        <div className="admin-modal-overlay" onClick={() => setResetCode(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h2>🎫 Password Reset Code</h2>
            <p style={{ fontSize: 14 }}>
              Share this code with <strong>{resetCode.user.name}</strong> ({resetCode.user.email}) —
              by phone, WhatsApp, or in person. They enter it at
              {' '}<strong>Sign in → Forgot password → Admin code</strong>.
            </p>
            <div style={{
              fontFamily: 'monospace', fontSize: 32, fontWeight: 700, letterSpacing: 4,
              textAlign: 'center', padding: '18px 0', margin: '10px 0',
              background: 'var(--surface2, rgba(99,102,241,0.08))', borderRadius: 10,
              userSelect: 'all',
            }}>
              {resetCode.code}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Expires {new Date(resetCode.expires_at).toLocaleTimeString()} (1 hour). One use only —
              generating a new code replaces this one. It won't be shown again after you close this.
            </p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => {
                navigator.clipboard?.writeText(resetCode.code)
                  .then(() => toast.success('Code copied'))
                  .catch(() => toast.error('Could not copy — select the code manually'));
              }}>📋 Copy code</button>
              <button className="btn-secondary" onClick={() => setResetCode(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="admin-modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h2>Edit User</h2>
            <label>Name</label>
            <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            <label>Email</label>
            <input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
            <label>Role</label>
            <select
              value={editForm.role}
              onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
              disabled={editForm.role === 'super_admin' && currentUser?.role !== 'super_admin'}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              {/* Only super admins can grant (or see) the super admin role */}
              {(currentUser?.role === 'super_admin' || editForm.role === 'super_admin') && (
                <option value="super_admin">Super Admin</option>
              )}
            </select>

            <label>Plan</label>
            <select value={editForm.plan} onChange={e => changePlan(e.target.value)}>
              {(accessConfig.plans.length ? accessConfig.plans : ['basic', 'pro', 'enterprise']).map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}{p === 'enterprise' ? ' / Custom' : ''}</option>
              ))}
            </select>

            <label>Page Access</label>
            {editForm.role === 'admin' || editForm.role === 'super_admin' ? (
              <p className="access-note">Admins have access to all pages.</p>
            ) : (
              <>
                <p className="access-note">
                  Choosing a plan pre-selects its pages. Tick or untick individual pages to customize.
                </p>
                <div className="page-access-grid">
                  {accessConfig.pages.map(pg => {
                    const locked = pg.key === 'dashboard'; // always available
                    const checked = locked || editForm.allowed_pages.includes(pg.key);
                    return (
                      <label key={pg.key} className={'page-access-item' + (locked ? ' locked' : '')}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={locked}
                          onChange={() => togglePage(pg.key)}
                        />
                        <span>{pg.label}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            <div className="modal-actions">
              <button className="btn-primary" onClick={saveEdit}>Save</button>
              <button className="btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {viewingUser && userDetail && (
        <div className="admin-modal-overlay" onClick={() => setViewingUser(null)}>
          <div className="admin-modal modal-wide" onClick={e => e.stopPropagation()}>
            <h2>{userDetail.user.name}</h2>
            <p className="detail-email">{userDetail.user.email}</p>
            <div className="detail-stats">
              <span>📇 {userDetail.stats.contactCount} contacts</span>
              <span>📢 {userDetail.stats.broadcastCount} broadcasts</span>
              <span>🏷 {userDetail.stats.tagCount} tags</span>
              <span>🔗 {userDetail.stats.linkCount} links</span>
              <span>⚡ {userDetail.stats.flowCount} flows</span>
            </div>
            <div className="detail-meta">
              <div><strong>Role:</strong> {userDetail.user.role}</div>
              <div><strong>Status:</strong> {userDetail.user.isActive ? 'Active' : 'Disabled'}</div>
              <div><strong>WA Connected:</strong> {userDetail.user.wa_config?.connected ? 'Yes' : 'No'}</div>
              {userDetail.user.wa_config?.business_name && (
                <div><strong>Business:</strong> {userDetail.user.wa_config.business_name}</div>
              )}
              <div><strong>Joined:</strong> {new Date(userDetail.user.createdAt).toLocaleString()}</div>
              <div><strong>Last active:</strong> {userDetail.user.last_active_at ? new Date(userDetail.user.last_active_at).toLocaleString() : 'Never'}</div>
            </div>
            {userDetail.recentBroadcasts.length > 0 && (
              <>
                <h3>Recent Broadcasts</h3>
                <ul className="detail-broadcasts">
                  {userDetail.recentBroadcasts.map(b => (
                    <li key={b._id}>
                      <span>{b.name}</span>
                      <span className={`status-badge ${b.status}`}>{b.status}</span>
                      <span>{b.channel}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => impersonate(viewingUser)}>🔑 Login as User</button>
              <button className="btn-secondary" onClick={() => setViewingUser(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WabaSharesCard({ users, pages }) {
  const [shares, setShares] = useState([]);
  const [ownerId, setOwnerId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [allowedPages, setAllowedPages] = useState(['dashboard', 'inbox', 'contacts']);
  const [shareTokens, setShareTokens] = useState(false);
  const [busy, setBusy] = useState(false);
  const shareablePages = pages.filter(page => !['settings', 'webhook-debug'].includes(page.key));
  const owners = users.filter(user => user.isActive && user.team_role !== 'agent' && user.wa_config?.connected);
  const members = users.filter(user => user.isActive && user.team_role !== 'agent' && user._id !== ownerId);
  const nameOf = id => users.find(user => user._id === id)?.name || id;
  const isAvailable = share => {
    const owner = users.find(user => user._id === share.owner);
    return !!owner?.isActive && !!owner.wa_config?.connected
      && owner.wa_config.waba_id === share.waba_id
      && owner.wa_config.phone_number_id === share.phone_number_id;
  };

  const load = useCallback(async () => {
    try { setShares((await api.get('/admin/waba-shares')).data.shares); }
    catch { toast.error('Could not load WABA links'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!ownerId || !memberId) return;
    setBusy(true);
    try {
      await api.post('/admin/waba-shares', {
        owner_id: ownerId, member_id: memberId,
        allowed_pages: allowedPages, share_tokens: shareTokens,
      });
      toast.success('WABA workspace linked');
      setMemberId('');
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not link accounts'); }
    setBusy(false);
  };

  const update = async (share, patch) => {
    setBusy(true);
    try {
      await api.patch(`/admin/waba-shares/${share._id}`, patch);
      toast.success('WABA link updated');
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not update link'); }
    setBusy(false);
  };

  const remove = async share => {
    if (!window.confirm(`Remove ${nameOf(share.member)} from ${nameOf(share.owner)}'s WABA workspace?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/waba-shares/${share._id}`);
      toast.success('WABA link removed');
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not remove link'); }
    setBusy(false);
  };

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-title">Shared WABA workspaces</div>
      <p>Link existing account owners to one connected WhatsApp workspace. Their own workspace remains available.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <label>WABA owner <select className="select" value={ownerId} onChange={e => setOwnerId(e.target.value)}>
          <option value="">Choose connected account</option>
          {owners.map(user => <option key={user._id} value={user._id}>{user.name} — {user.email}</option>)}
        </select></label>
        <label>Person to link <select className="select" value={memberId} onChange={e => setMemberId(e.target.value)}>
          <option value="">Choose account</option>
          {members.map(user => <option key={user._id} value={user._id}>{user.name} — {user.email}</option>)}
        </select></label>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '12px 0' }}>
        {shareablePages.map(page => <label key={page.key}>
          <input type="checkbox" checked={allowedPages.includes(page.key)}
            onChange={e => setAllowedPages(current => e.target.checked ? [...current, page.key] : current.filter(key => key !== page.key))} /> {page.label}
        </label>)}
      </div>
      <label><input type="checkbox" checked={shareTokens} onChange={e => setShareTokens(e.target.checked)} /> Share WABA owner's sending tokens</label>
      <div><button className="btn btn-primary" disabled={busy || !ownerId || !memberId} onClick={create}>Link person</button></div>
      <div className="table-wrap" style={{ marginTop: 16 }}><table>
        <thead><tr><th>WABA owner</th><th>Linked person</th><th>Shared WABA / phone ID</th><th>Status</th><th>Token payer</th><th>Pages</th><th>Actions</th></tr></thead>
        <tbody>{shares.map(share => <tr key={share._id}>
          <td>{nameOf(share.owner)}</td><td>{nameOf(share.member)}</td>
          <td>{share.waba_id} / {share.phone_number_id}</td>
          <td>{isAvailable(share) ? 'Active' : 'Suspended — source connection changed'}</td>
          <td>{share.share_tokens ? 'WABA owner' : 'Linked person'}</td>
          <td>{share.allowed_pages.join(', ')}</td>
          <td>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => update(share, { share_tokens: !share.share_tokens })}>
              {share.share_tokens ? 'Use own tokens' : 'Share owner tokens'}
            </button>{' '}
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => {
              const entered = window.prompt('Comma-separated page keys', share.allowed_pages.join(', '));
              if (entered !== null) update(share, { allowed_pages: entered.split(',').map(page => page.trim()).filter(Boolean) });
            }}>Edit pages</button>{' '}
            {!isAvailable(share) && <><button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => update(share, { approve_waba: true })}>Approve current WABA</button>{' '}</>}
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => remove(share)}>Unlink</button>
          </td>
        </tr>)}</tbody>
      </table></div>
    </div>
  );
}

function TokenBillingCard({ users, onChanged }) {
  const [settings, setSettings] = useState(null);
  const [meta, setMeta] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [adjustment, setAdjustment] = useState('');
  const [reason, setReason] = useState('Token top-up');
  const [margin, setMargin] = useState('');
  const [busy, setBusy] = useState(false);
  const owners = users.filter(u => u.team_role !== 'agent' || !u.workspace_owner);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/settings/billing');
      setSettings(r.data.billing);
      setMeta(r.data);
    } catch { /* only rendered for super admins */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const r = await api.put('/admin/settings/billing', {
        ...settings,
        tokens_per_usd: Number(settings.tokens_per_usd),
        default_margin_percent: Number(settings.default_margin_percent),
        response_tokens: Number(settings.response_tokens),
      });
      setSettings(r.data.billing);
      toast.success('Token billing settings saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save billing settings'); }
    setBusy(false);
  };

  const applyAdjustment = async () => {
    if (!accountId || !adjustment) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${accountId}/tokens`, { adjustment: Number(adjustment), reason });
      toast.success(`${Number(adjustment) > 0 ? 'Added' : 'Removed'} ${Math.abs(Number(adjustment)).toLocaleString()} tokens`);
      setAdjustment('');
      onChanged?.();
      window.dispatchEvent(new Event('token-balance-refresh'));
    } catch (err) { toast.error(err.response?.data?.error || 'Token adjustment failed'); }
    setBusy(false);
  };

  const saveMargin = async () => {
    if (!accountId) return;
    setBusy(true);
    try {
      await api.put(`/admin/users/${accountId}/billing`, {
        margin_percent: margin === '' ? null : Number(margin),
      });
      toast.success(margin === '' ? 'Account now inherits the default margin' : 'Account margin saved');
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.error || 'Margin update failed'); }
    setBusy(false);
  };

  const selectAccount = id => {
    setAccountId(id);
    const selected = users.find(u => u._id === id);
    setMargin(selected?.token_margin_percent ?? '');
  };

  if (!settings) return null;

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-title">Token billing &amp; margin</div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
        Template charges use the configured USD rate and markup for each dialing prefix and category.
        Service replies use the configured token charge. Failed WhatsApp sends are refunded automatically.
        {meta?.rate_card_effective_at && <> Initial rate card: <strong>{meta.rate_card_effective_at}</strong>.</>}
      </p>

      <div className="billing-settings-grid">
        <label className="label">Tokens per USD
          <input className="input" type="number" min="1" value={settings.tokens_per_usd}
            onChange={e => setSettings(s => ({ ...s, tokens_per_usd: e.target.value }))} />
        </label>
        <label className="label">Default margin %
          <input className="input" type="number" min="0" max="1000" step="0.1" value={settings.default_margin_percent}
            onChange={e => setSettings(s => ({ ...s, default_margin_percent: e.target.value }))} />
        </label>
        <label className="label">Tokens per service reply
          <input className="input" type="number" min="1" value={settings.response_tokens}
            onChange={e => setSettings(s => ({ ...s, response_tokens: e.target.value }))} />
        </label>
        <label className="billing-toggle">
          <input type="checkbox" checked={settings.enabled !== false}
            onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))} /> Billing enabled
        </label>
      </div>
      <button className="btn btn-primary" disabled={busy} onClick={saveSettings}>Save billing settings</button>

      <div className="table-wrap billing-account-table">
        <table>
          <thead><tr><th>Workspace</th><th>Balance</th><th>Total used</th><th>Margin</th></tr></thead>
          <tbody>{owners.map(u => (
            <tr key={u._id}>
              <td>{u.name}<div className="user-cell-email">{u.email}</div></td>
              <td>{new Intl.NumberFormat().format(u.token_balance || 0)}</td>
              <td>{new Intl.NumberFormat().format(u.token_used_total || 0)}</td>
              <td>{u.token_margin_percent == null ? `Default (${settings.default_margin_percent}%)` : `${u.token_margin_percent}%`}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <div className="billing-account-controls">
        <label className="label">Workspace
          <select className="select" value={accountId} onChange={e => selectAccount(e.target.value)}>
            <option value="">Choose an account…</option>
            {owners.map(u => <option key={u._id} value={u._id}>{u.name} — {u.email}</option>)}
          </select>
        </label>
        <label className="label">Token adjustment
          <input className="input" type="number" step="1" placeholder="e.g. 10000 or -500"
            value={adjustment} onChange={e => setAdjustment(e.target.value)} />
        </label>
        <label className="label">Reason
          <input className="input" value={reason} onChange={e => setReason(e.target.value)} />
        </label>
        <button className="btn btn-primary" disabled={busy || !accountId || !adjustment} onClick={applyAdjustment}>Apply tokens</button>
      </div>
      <div className="billing-margin-controls">
        <label className="label">Account margin override %
          <input className="input" type="number" min="0" max="1000" step="0.1" placeholder="Blank = default"
            value={margin} onChange={e => setMargin(e.target.value)} />
        </label>
        <button className="btn btn-ghost" disabled={busy || !accountId} onClick={saveMargin}>Save account margin</button>
      </div>
      <BillingRateTable settings={settings}
        accountMargin={owners.find(owner => owner._id === accountId)?.token_margin_percent ?? null} />
    </div>
  );
}

// ── Public API rate limits (super admin only) ────────────────────────────────
// The platform default applies to every API key; an account can be given its
// own ceiling — for a partner integrating heavily, or one hammering the API.
// 0 means unlimited. The server re-reads these within 30 seconds, so a change
// takes effect without a restart.
function ApiLimitsCard({ users, onChanged }) {
  const [settings, setSettings] = useState(null); // { default_rpm, enabled }
  const [overrides, setOverrides] = useState([]);
  const [busy, setBusy] = useState(false);
  const [grantTo, setGrantTo] = useState('');
  const [grantRpm, setGrantRpm] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/settings/api');
      setSettings(r.data.api);
      setOverrides(r.data.overrides || []);
    } catch { /* non-super-admins never render this card */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put('/admin/settings/api', { ...settings, ...patch });
      setSettings(r.data.api);
      toast.success('API settings saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    setBusy(false);
  };

  const setUserLimit = async (userId, rpm) => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${userId}/api-limit`, { rpm });
      toast.success(rpm === null ? 'Override removed' : `Limit set to ${rpm === 0 ? 'unlimited' : `${rpm}/min`}`);
      setGrantTo(''); setGrantRpm('');
      load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setBusy(false);
  };

  if (!settings) return null;

  return (
    <div className="card" style={{ marginTop: 24, maxWidth: 640 }}>
      <div className="card-title">🔌 Public API — access &amp; rate limits</div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0, lineHeight: 1.7 }}>
        Users generate their own API keys in Settings → API Access. Requests are counted per key in
        a rolling minute. Set <code>0</code> for no limit. Changes apply within 30 seconds — no
        restart needed.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="label">Default limit (requests/minute)</label>
          <input className="input" type="number" min={0} max={100000} style={{ width: 160 }}
            value={settings.default_rpm}
            onChange={e => setSettings(s => ({ ...s, default_rpm: e.target.value }))}
            onBlur={e => save({ default_rpm: Number(e.target.value) || 0 })} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, paddingBottom: 8 }}>
          <input type="checkbox" checked={settings.enabled !== false} disabled={busy}
            onChange={e => save({ enabled: e.target.checked })} />
          API enabled platform-wide
        </label>
      </div>

      {settings.enabled === false && (
        <div className="alert alert-warning" role="alert">
          The API is switched off. Every request returns 503 until you turn it back on.
        </div>
      )}

      <h4 style={{ fontSize: 13, margin: '18px 0 8px' }}>Per-account limits</h4>
      {overrides.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          No account overrides — everyone is on the default.
        </p>
      ) : (
        <div className="table-wrap" style={{ marginBottom: 12 }}>
          <table>
            <thead>
              <tr><th>Account</th><th>Limit</th><th>Key last used</th><th /></tr>
            </thead>
            <tbody>
              {overrides.map(u => (
                <tr key={u._id}>
                  <td>{u.name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.email}</div></td>
                  <td>{u.api_rate_limit_rpm === 0 ? 'Unlimited' : `${u.api_rate_limit_rpm}/min`}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {u.api_key_last_used_at ? new Date(u.api_key_last_used_at).toLocaleString() : 'never'}
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" disabled={busy}
                      onClick={() => setUserLimit(u._id, null)}>Reset to default</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="label">Account</label>
          <select className="select" style={{ width: 220 }} value={grantTo}
            onChange={e => setGrantTo(e.target.value)}>
            <option value="">Choose an account…</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name} — {u.email}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="label">Requests/minute</label>
          <input className="input" type="number" min={0} max={100000} style={{ width: 130 }}
            placeholder="0 = unlimited" value={grantRpm} onChange={e => setGrantRpm(e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={busy || !grantTo || grantRpm === ''}
          onClick={() => setUserLimit(grantTo, Number(grantRpm))}>Set limit</button>
      </div>
    </div>
  );
}
